/**
 * Artifact error banner — shown when a create/update stream fails.
 *
 * Before issue #961 a failed update simply left the canvas spinning forever
 * (the runtime never emitted a finish event on error). Now the runtime emits a
 * `data-artifact-error` event, the store flips to `status: "error"` while
 * keeping the previous body intact, and this banner surfaces a short message
 * plus a one-click **Retry** that re-issues the same instruction through the
 * chat. Self-gating — renders nothing unless the canvas is in the error state.
 *
 * @module components/artifacts/artifact-error-banner
 */

import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, RotateCw, X } from "lucide-react";
import { useInteractiveChat } from "@/components/chat/interactive/interactive-context";
import { Button } from "@/components/ui/button";
import { useArtifactStore } from "@/store/artifact-store";

/**
 * Floating error banner pinned to the top of the canvas body. Mount it once
 * per canvas; it shows itself only while `status === "error"`.
 */
export function ArtifactErrorBanner() {
  const status = useArtifactStore((s) => s.artifact.status);
  const error = useArtifactStore((s) => s.artifact.error);
  const lastInstruction = useArtifactStore((s) => s.artifact.lastInstruction);
  const dismissError = useArtifactStore((s) => s.dismissError);
  const { sendMessage } = useInteractiveChat();

  const isError = status === "error";

  /**
   * Re-issue the failed edit through the chat so the model runs `update_artifact`
   * again. We dismiss optimistically — the resulting init event flips the
   * canvas back into its streaming state on its own.
   */
  function handleRetry() {
    const instruction = lastInstruction?.trim();
    sendMessage(instruction && instruction.length > 0 ? instruction : "Please try updating the artifact again.");
    dismissError();
  }

  return (
    <AnimatePresence>
      {isError ? (
        <motion.div
          key="artifact-error-banner"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-x-3 top-3 z-40"
        >
          <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 shadow-lg backdrop-blur">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">Couldn't finish the update</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {error || "Something went wrong. Your previous version is intact."}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <Button type="button" size="sm" variant="default" className="h-7 gap-1.5" onClick={handleRetry}>
                <RotateCw className="size-3.5" />
                Retry
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-7"
                onClick={dismissError}
                title="Dismiss"
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
